import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-icon.js';
import { FormField } from '@adornis/formfield/form-field.js';
import '@adornis/popover/x-dropdown-selection.js';
import '@adornis/popover/x-tooltip.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { combineLatest, map } from 'rxjs';
import type { ExamDay } from '../../../src/db/examdays.js'; // TODO this crosses the module boundary and has to be refactored!
import type { OSCE } from '../db/osce.js';
import './ils-osce-type.js';

/**
 * @element ils-osce-picker
 */
@customElement('ils-osce-picker')
export class IlsOscePicker extends FormField<OSCE | string> {
  @property() mode: 'entity' | 'id' = 'id';
  @property({ attribute: false }) examDay?: ExamDay;
  @property({ attribute: false }) osceFilter = new RXController<((osce: OSCE) => boolean) | null>(this, null);

  @property({ attribute: false }) allOSCEs = new RXController<OSCE[]>(this, []);
  @state() private readonly _filteredOSCEs = new RXController(
    this,
    combineLatest([this.allOSCEs.observable, this.osceFilter.observable]),
    obs$ =>
      obs$.pipe(
        map(([allOsces, filterFn]) => {
          if (filterFn) return allOsces.filter(filterFn);
          return allOsces;
        }),
      ),
  );

  override render() {
    return html`
      <x-dropdown-selection
        placeholder=${this.value.value ? 'OSCE ändern...' : 'OSCE auswählen...'}
        .value=${this.mode === 'entity'
          ? this.value.value
          : (this.allOSCEs.value ?? []).find(osce => osce._id === this.value.value)}
        @value-picked=${(e: ValueEvent<OSCE>) => {
          if (!e.detail.value) {
            this.value.next(null);
            this.dispatchEvent(new CustomEvent('value-picked', { detail: { value: this.value.value } }));
            return;
          }

          if (this.mode === 'id') {
            this.value.next(e.detail.value._id);
          } else {
            this.value.next(e.detail.value);
          }
          this.dispatchEvent(new CustomEvent('value-picked', { detail: { value: this.value.value } }));
        }}
        .search=${(search: string) =>
          search
            ? this._filteredOSCEs.value?.filter(osce => osce.title.toLowerCase().includes(search.toLowerCase()))
            : this._filteredOSCEs.value}
        .renderString=${(osce: Maybe<OSCE>) => {
          if (!osce) return '';
          return this._osceDropdownContent(osce);
        }}
        .renderItem=${(osce: OSCE) => this._osceDropdownContent(osce)}
        .emptyState=${html`<x-text>Keine OSCEs gefunden</x-text>`}
      >
      </x-dropdown-selection>
    `;
  }

  private _osceDropdownContent(osce: OSCE) {
    const warning = this.examDay?.participatedOSCEs?.includes(osce._id);
    return html`
      <x-flex
        horizontal
        space-between
        crossaxis-center
        space="xxs"
        padding="sm"
        ${css({ whiteSpace: 'nowrap', overflow: 'hidden' })}
      >
        <x-text
          ${css({
            padding: '4px 0',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: warning ? this.colors.tone.warning : undefined,
          })}
        >
          ${osce?.title}
        </x-text>
        ${warning
          ? html`
              <x-icon ${css({ color: this.colors.tone.warning, marginRight: '0.2em' })}> warning </x-icon>
              <x-tooltip
                .text=${html`
                  <x-text> </x-text>
                  <x-text> Ein Prüfling hat an diesem OSCE bereits teilgenommen </x-text>
                `}
              ></x-tooltip>
            `
          : html` <ils-osce-type .osce=${osce}></ils-osce-type> `}
      </x-flex>
    `;
  }
}
